import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, CheckCircle2, XCircle, PauseCircle, PenLine, ExternalLink,
  ShieldAlert, Save, Users, GitBranch, Trash2, RotateCcw, PackageCheck, Clock,
  FileText, Package,
} from "lucide-react";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import AuditTrail from "@/components/AuditTrail";
import ActionModal from "@/components/ActionModal";
import TransportPOActionModal from "@/components/TransportPOActionModal";
import TransportPOAckModal from "@/components/TransportPOAckModal";
import TransportAccountsAckModal from "@/components/TransportAccountsAckModal";
import TransportFinalPOFulfillModal from "@/components/TransportFinalPOFulfillModal";
import TransportAccountsFinalModal from "@/components/TransportAccountsFinalModal";
import AccountsPOActionModal from "@/components/AccountsPOActionModal";
import ServicePOActionModal from "@/components/ServicePOActionModal";
import FnBPOActionModal from "@/components/FnBPOActionModal";
import PrintPOActionModal from "@/components/PrintPOActionModal";
import PrintRowsAttachments from "@/components/PrintRowsAttachments";
import StoresPOActionModal from "@/components/StoresPOActionModal";
import FinalBillsModal from "@/components/FinalBillsModal";
import CompletionToggleModal from "@/components/CompletionToggleModal";
import Admin1PricingModal from "@/components/Admin1PricingModal";
import EditSectionModal from "@/components/EditSectionModal";
import FinalPOFanoutModal from "@/components/FinalPOFanoutModal";
import SingleFulfilmentModal from "@/components/SingleFulfilmentModal";
import AmendDecisionModal from "@/components/AmendDecisionModal";
import { FormDetails, FORM_TYPE_LABEL } from "@/components/FormDetails";
import VendorListTable from "@/components/VendorListTable";
import LateBanner from "@/components/LateBanner";
import TatBadge from "@/components/TatBadge";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import { RE_EDIT_SCHEMAS, getReeditSchema } from "@/lib/reeditSchemas";
import { formatDate } from "@/lib/dateFmt";
import api, { formatApiError } from "@/lib/api";
import {
  STAGE_LABEL, WORKFLOW_STAGES,
  TRANSPORTATION_WORKFLOW_STAGES,      // back-compat (defaults to v5.2)
  getTransportationStages,             // Phase 5.2 — pick strip by workflow_version
  getFormStages,                        // Phase 6 — form_type-aware stage selector
  getStageLabelForForm,                 // Phase 12 — per-form label override
} from "@/lib/statusMap";

export default function RequisitionDetail() {
  const { ref } = useParams();
  const { user, config } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  // Phase 17 (item 6) — Amend-decision modal state. Declared here (above
  // the early-returns) so hook order stays identical on every render.
  const [showAmend, setShowAmend] = useState(false);
  const [amendPrior, setAmendPrior] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(`/requisitions/${ref}`)
      .then((r) => setReq(r.data))
      .catch(() => setReq(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [ref]);

  if (loading) return <Layout><div className="p-10 text-center text-[#6e7770]">Loading…</div></Layout>;
  if (!req) return <Layout><div className="p-10 text-center text-[#b3261e]">Requisition not found.</div></Layout>;

  const isCurrentApprover = req.current_approvers?.includes(user.id);
  const isApplicant = req.applicant_id === user.id;
  const isTerminal = ["completed", "rejected"].includes(req.status);
  const isSuper = isSuperAdmin(user);
  // Phase 5.2 — super_admin can act on any non-terminal stage regardless of
  // whether they are the assigned approver. Yellow "acting as" banner shows
  // inside the modal to make the bypass unambiguous in the audit trail.
  const canAct = (!isTerminal) && (isCurrentApprover || isSuper);
  const superBypassing = isSuper && !isCurrentApprover && !isTerminal;
  const canResubmit = isApplicant && req.status === "on_hold";

  // Phase 17 (item 6) — Amend-decision button exposure.
  // Visible when: (a) current user has at least one approve/on_hold/reject
  // audit entry on this req AND the req isn't completed; OR (b) user is
  // super_admin (can amend ANY stage via server-side powers).
  const _amendableActions = new Set(["approve", "on_hold", "reject"]);
  const myPriorActions = (req.audit_trail || []).filter(
    (a) => a && _amendableActions.has(a.action) && a.actor_id === user.id,
  );
  const canAmendOwnDecision = !isTerminal && myPriorActions.length > 0;
  const canAmendAnyStage = !isTerminal && isSuper;

  // Phase 5.2 & 6 — Stage strip selection via form_type-aware helper.
  const isTransport = req.form_type === "transportation";
  const isService = req.form_type === "service";
  const wfv = req.workflow_version || "5.0";
  const stagesToShow = getFormStages(req.form_type, wfv);
  const stageIdx = Math.max(0, stagesToShow.indexOf(req.current_stage));

  // Phase 5.2 — grey-out the Admin-2 pill when it was intentionally skipped
  // (imprest / parents payment_source). Also grey-out when the stage was
  // simply not-yet-reached and imprest/parents is already decided.
  const admin2Skipped = isTransport
    && (req.admin2_skipped_at || (
      wfv === "5.2"
      && ["student_imprest", "parents"].includes(
          (req.form_data?.payment_source || "").toLowerCase(),
        )
    ));

  // Phase 5 / 5.2 — Determine which Phase-5-family modal (if any) to use.
  const transportStage = isTransport ? req.current_stage : null;
  const isTransportPOStage = transportStage === "transport_po_review";
  // Phase 22b — Transport v22 stage detectors.
  const isTransportV22 = isTransport && (req.workflow_version || "") === "22.0";
  const isV22POAckStage = isTransportV22 && transportStage === "transport_po_ack";
  const isV22AccountsAckStage = isTransportV22 && transportStage === "transport_accounts_ack";
  const isV22FinalPOStage = isTransportV22 && transportStage === "transport_final_po_fulfill";
  const isV22AccountsFinalStage = isTransportV22 && transportStage === "transport_accounts_final";
  const isAccountsPOStage  = transportStage === "accounts_po_review"
    || req.current_stage === "accounts_imprest_check";  // Phase 15.1 — Sangita's imprest stage uses same modal (Yes/No + remark)
  const isFinalPOStage     = transportStage === "final_po_fanout";       // v5.0 legacy
  const isProcessOwnerReview = req.current_stage === "process_owner_review"
                                && (isTransport || isService || req.form_type === "maini"
                                    || ((req.form_type === "hotel" || req.form_type === "travel")
                                        && (req.workflow_version || "") === "13.0"));
  const isAccountsReview    = req.current_stage === "accounts_review"
                                && (isTransport || isService
                                    || ((req.form_type === "hotel" || req.form_type === "travel")
                                        && (req.workflow_version || "") === "13.0"));
  // Phase 15 — Completion toggle (final PO stage for 7 form types).
  const _finalStageForType = {
    material: "process_owner_review",
    service: "process_owner_review",
    transportation: "process_owner_review",
    fnb: (req.workflow_version || "") === "15.0"
      ? "fnb_po_completion" : "process_owner_review",
    pocket_money: (req.workflow_version || "") === "16.0"
      ? "process_owner_review" : "accounts_review",
    advance_requisition: "accounts_review",
    advance_settlement: "accounts_review",
  }[req.form_type];
  const isCompletionToggleStage =
    _finalStageForType && req.current_stage === _finalStageForType
    && req.status !== "completed";
  // Phase 6 — Service PO stage (mirrors transport_po_review, different actor)
  const isServicePOStage = isService && req.current_stage === "service_po_review";
  // Phase 10 — Stores PO stage (CAO or Arun.D one-of gate) for Material & Service v10.
  const isV10MatSvc = (req.form_type === "material" || req.form_type === "service")
    && ["10.0", "11.0"].includes(req.workflow_version || "");
  const isStoresPOStage = isV10MatSvc && req.current_stage === "stores_po_review";
  const isV10AccountsReview = isV10MatSvc && req.current_stage === "accounts_review";
  // Phase 13 — Hotel/Travel v13 accounts_review: same FinalBillsModal (optional attachments).
  const isHotelTravelV13 = (req.form_type === "hotel" || req.form_type === "travel")
    && (req.workflow_version || "") === "13.0";
  const isV13AccountsReview = isHotelTravelV13 && req.current_stage === "accounts_review";
  // Phase 7 — F&B PO stage (Sandeep) + Admin-1 pricing stage for Hotel/Travel v7.
  const isFnb = req.form_type === "fnb";
  const isFnbPOStage = isFnb && req.current_stage === "fnb_po_review";
  const isHotelOrTravelV7 = (req.form_type === "hotel" || req.form_type === "travel")
    && (req.workflow_version || "") === "7.0";
  const isAdmin1PricingStage = isHotelOrTravelV7 && req.current_stage === "admin1_review";
  // Phase 20.2 — Print Job PO stage detection.
  const isPrintJob = req.form_type === "print_job";
  const isPrintPOStage = isPrintJob && req.current_stage === "print_fulfillment";
  const printPOStatusKey = (req.form_data || {}).print_po_status;
  const printPOStatusLabel = (req.form_data || {}).print_po_status_label;
  // Phase 7 — re-edit gating
  const RE_EDIT_STAGES = new Set(["submitted", "hod_review", "transport_po_review",
                                    "service_po_review", "fnb_po_review",
                                    "stores_po_review", "admin1_review"]);
  const canReEdit = !isTerminal && (
    (isApplicant && RE_EDIT_STAGES.has(req.current_stage)) || isSuper
  );

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <button className="link-vgws text-sm inline-flex items-center gap-1 mb-4" onClick={() => nav(-1)} data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <LateBanner show={!!req.late_submission} />
      {req.is_emergency && (
        <div
          className="mb-4 rounded-md overflow-hidden shadow-sm"
          data-testid="emergency-banner"
        >
          <div className="px-4 py-3 bg-[#b3261e] text-white uppercase tracking-wider text-sm font-bold flex items-center gap-2">
            🚨 Emergency Requisition — expedited handling requested
          </div>
          {req.emergency_reason && (
            <div className="px-4 py-2 bg-[#fcebe9] text-[#7a1f2b] text-sm border-t border-[#f2c8c4]"
                 data-testid="emergency-reason-text">
              <strong>Reason:</strong> {req.emergency_reason}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Requisition</div>
          <h1 className="font-serif text-3xl font-bold text-[#7a1f2b] flex items-center gap-3" data-testid="req-ref-code">
            {req.ref_code}
            {canReEdit && (
              <button type="button" onClick={() => setShowEdit(true)}
                      className="text-[#7a1f2b] hover:bg-[#faf5eb] rounded p-1"
                      title="Re-edit requisition (until Admin-2 approves)"
                      data-testid="re-edit-icon">
                <PenLine size={18} />
              </button>
            )}
            {req.form_data_version && req.form_data_version > 1 && (
              <span className="text-[10px] uppercase tracking-widest bg-[#faf5eb] border border-[#b48a45] text-[#8a6a2f] rounded-full px-2 py-0.5"
                    data-testid="edited-badge"
                    title={`Edited ${req.form_data_version - 1} time(s)`}>
                Edited ×{req.form_data_version - 1}
              </span>
            )}
            {req.parent_ref_code && (
              <span className="text-[10px] uppercase tracking-widest bg-[#e6f2f0] border border-[#2d6b6b] text-[#2d6b6b] rounded-full px-2 py-0.5"
                    data-testid="resubmitted-from-pill"
                    title={`Resubmitted from ${req.parent_ref_code}`}>
                Resubmitted from {req.parent_ref_code}
              </span>
            )}
          </h1>
          <div className="text-sm text-[#4a524c] mt-1" data-testid="req-form-type">
            {FORM_TYPE_LABEL[req.form_type] || req.form_type} · Submitted {formatDate(req.submitted_at || req.created_at, { includeTime: true })}
          </div>
          {/* Phase 20.2 — Print Job PO status badge (visible once the PO
              has set a status, including after lock). Prominent so the
              applicant/HOD immediately see the latest state. */}
          {isPrintJob && printPOStatusKey && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#faf5eb] border border-[#b48a45] rounded-full px-3 py-1"
                 data-testid="print-po-status-badge">
              <span className="text-[10px] uppercase tracking-widest text-[#8a6a2f] font-semibold">
                Print PO status
              </span>
              <span className="text-xs text-[#7a1f2b] font-semibold">
                {printPOStatusLabel || printPOStatusKey}
              </span>
            </div>
          )}
          {/* Phase 10 — universal Download CTAs (form PDF / audit PDF / all attachments zip) */}
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="download-ctas">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requisitions/${req.ref_code}/pdf/form`}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7a1f2b] text-[#7a1f2b] hover:bg-[#7a1f2b] hover:text-white transition-colors text-xs font-semibold"
               data-testid="download-form-pdf">
              <FileText size={14} /> Download Form
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requisitions/${req.ref_code}/pdf/audit`}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#2d6b6b] text-[#2d6b6b] hover:bg-[#2d6b6b] hover:text-white transition-colors text-xs font-semibold"
               data-testid="download-audit-pdf">
              <FileText size={14} /> Download Audit Trail
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requisitions/${req.ref_code}/attachments/zip`}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#b48a45] text-[#8a6a2f] hover:bg-[#b48a45] hover:text-white transition-colors text-xs font-semibold"
               data-testid="download-attachments-zip">
              <Package size={14} /> Download Attachments
            </a>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <TatBadge deadline={req.tat_deadline} current_stage={req.current_stage} status={req.status} />
          <StatusBadge status={req.status} formType={req.form_type} />
          <div className="text-xs text-[#6e7770] text-right">
            <div>Current stage</div>
            <div className="font-medium text-[#1f2320]" data-testid="req-current-stage">
              {STAGE_LABEL[req.current_stage] || req.current_stage}
            </div>
          </div>
        </div>
      </div>

      {/* Stage strip */}
      <div className="sheet-plain p-4 sm:p-5 mb-6 overflow-x-auto stage-timeline" data-testid="stage-strip">
        <div className="flex items-center gap-2 min-w-max">
          {stagesToShow.map((s, i) => {
            const state = i < stageIdx ? "done" : i === stageIdx ? "active" : "pending";
            const isRejected = req.status === "rejected" && i > stageIdx;
            // Phase 5.2 — grey-out the Admin-2 (HOS) pill when it was
            // intentionally skipped for this ticket (imprest / parents).
            const isSkippedAdmin2 = s === "admin2_review" && admin2Skipped;
            // Phase 8.3 — surface self-skip pill on the skipped stage row.
            const selfSkipEntry = (req.audit_trail || []).find(
              (a) => a?.type === "self_skip" && a?.stage === s,
            );
            const selfSkipReason = selfSkipEntry
              ? (s === "hod_review"
                  ? "Auto-skipped (Self-declared HOD)"
                  : s === "admin1_review"
                    ? "Auto-skipped (Self-declared Admin-1)"
                    : "Auto-skipped (Self-declared)")
              : null;
            const label = isSkippedAdmin2 ? "Admin-2 (Skipped)" : getStageLabelForForm(req.form_type, s);
            const textCls = isSkippedAdmin2
              ? "text-[#a09b90] line-through decoration-[#a09b90]/60"
              : (state === "active" ? "text-[#7a1f2b]"
                : state === "done"  ? "text-[#15803d]" : "text-[#6e7770]");
            return (
              <div key={s} className="flex items-center gap-2" data-testid={`stage-pill-${s}`}>
                <div className={`stage-dot ${state} ${isSkippedAdmin2 ? "opacity-40" : ""}`}></div>
                <div className={`text-xs font-medium whitespace-nowrap ${textCls}`}>
                  {label}
                </div>
                {selfSkipReason && (
                  <span
                    className="ml-1 inline-flex items-center rounded-full bg-[#efeae0] border border-[#d6cbb1] px-2 py-0.5 text-[10px] font-medium text-[#6e7770] whitespace-nowrap"
                    title={selfSkipEntry?.remarks || selfSkipReason}
                    data-testid={`self-skip-pill-${s}`}
                  >
                    {selfSkipReason}
                  </span>
                )}
                {i < stagesToShow.length - 1 && (
                  <div className={`h-px w-8 ${state === "done" ? "bg-[#16a34a]" : "bg-[#e2d7c3]"} ${isRejected ? "bg-[#b3261e]/40" : ""}`}></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,420px] gap-6">
        <div className="space-y-6">
          <FormDetails req={req} />

          {/* Phase 11 — Vendor list table on the 7 procurement-driven forms. */}
          {["material", "service", "event", "transportation",
            "fnb", "travel", "hotel"].includes(req.form_type) && (
            <VendorListTable requisition={req} currentUser={user} onUpdated={() => load?.()} />
          )}

          {/* Phase 21.5 — Per-row attachment downloads for Print Job. */}
          {isPrintJob && (
            <PrintRowsAttachments
              refCode={req.ref_code}
              printRows={(req.form_data || {}).print_rows || []}
            />
          )}

          {req.current_stage === "process_owner" && req.process_owner_sub_status && (
            <div className="sheet p-5 border-t-[#b48a45]" data-testid="sub-status-panel">
              <div className="text-xs uppercase tracking-widest text-[#b48a45] font-semibold">Procurement sub-status</div>
              <div className="font-serif text-lg text-[#1f2320] mt-1">{req.process_owner_sub_status}</div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {canAct && (
            <div className="sheet p-5" data-testid="action-panel">
              <h3 className="font-serif text-lg font-semibold text-[#7a1f2b] mb-3">Your action required</h3>
              {/* Phase 19.1 — Acting-as banner is now rendered only inside the
                  action modal (via the `actingAsBanner` prop) to avoid a
                  duplicate render on the detail page. */}
              <p className="text-sm text-[#4a524c] mb-4">
                {superBypassing
                  ? <>You are the <strong>super admin</strong> — you may act at <strong>{STAGE_LABEL[req.current_stage]}</strong>.</>
                  : <>You are the current approver at <strong>{STAGE_LABEL[req.current_stage]}</strong>.</>}
                {isFinalPOStage
                  ? " Both Accounts (Amey.G) and Transport (Shivmohan.V) must mark this fulfilled before it can close."
                  : (isProcessOwnerReview || isAccountsReview)
                    ? " Use Mark Fulfilled — remarks are optional here."
                    : " Approve remarks are optional; hold/reject remarks are mandatory."}
              </p>
              <div className="flex flex-col gap-2">
                {isPrintPOStage && (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("print_po_update")}
                          data-testid="print-po-update-button">
                    <PackageCheck size={16} /> Update Print status
                  </button>
                )}
                {req.current_stage === "process_owner" && (
                  <button type="button" className="btn-vgws" onClick={() => setAction("update_status")} data-testid="update-status-button">
                    <PenLine size={16} /> Update procurement status
                  </button>
                )}
                {isFinalPOStage ? (
                  <>
                    {/* v5.0 legacy — Twin fulfilment status banners */}
                    <div className="text-xs bg-[#faf5eb] border border-[#e2d7c3] rounded p-3 space-y-1"
                         data-testid="fulfilment-status-panel">
                      <div className="flex items-center gap-2">
                        {req.fulfilled_by_accounts_at
                          ? <CheckCircle2 size={12} className="text-[#2d6b6b]" />
                          : <Clock size={12} className="text-[#b48a45]" />}
                        <span>
                          Accounts (<strong>{req.accounts_name || "Amey.G"}</strong>):{" "}
                          {req.fulfilled_by_accounts_at
                            ? `Fulfilled ${formatDate(req.fulfilled_by_accounts_at, { includeTime: true })}`
                            : "pending"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.fulfilled_by_transport_at
                          ? <CheckCircle2 size={12} className="text-[#2d6b6b]" />
                          : <Clock size={12} className="text-[#b48a45]" />}
                        <span>
                          Transport (<strong>{req.transport_po_name || "Shivmohan.V"}</strong>):{" "}
                          {req.fulfilled_by_transport_at
                            ? `Fulfilled ${formatDate(req.fulfilled_by_transport_at, { includeTime: true })}`
                            : "pending"}
                        </span>
                      </div>
                    </div>
                    <button type="button" className="btn-vgws btn-teal"
                            onClick={() => setAction("mark_fulfilled")}
                            data-testid="mark-fulfilled-button">
                      <PackageCheck size={16} /> Mark Fulfilled
                    </button>
                  </>
                ) : (isProcessOwnerReview || isAccountsReview) ? (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("mark_fulfilled")}
                          data-testid="mark-fulfilled-button">
                    <PackageCheck size={16} /> Mark Fulfilled
                  </button>
                ) : isCompletionToggleStage ? (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("set_completion_status")}
                          data-testid="completion-toggle-button">
                    <CheckCircle2 size={16} /> Update completion status
                  </button>
                ) : isV22POAckStage ? (
                  // Phase 22b — Transport v22.0 PO Acknowledge CTA.
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("transport_po_acknowledge")}
                          data-testid="transport-po-ack-button">
                    <CheckCircle2 size={16} /> Acknowledged
                  </button>
                ) : isV22AccountsAckStage ? (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("transport_accounts_ack")}
                          data-testid="transport-accounts-ack-button">
                    <CheckCircle2 size={16} /> Accounts Ack
                  </button>
                ) : isV22FinalPOStage ? (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("transport_final_po_fulfill")}
                          data-testid="transport-final-po-button">
                    <PackageCheck size={16} /> Final PO Fulfilment
                  </button>
                ) : isV22AccountsFinalStage ? (
                  <button type="button" className="btn-vgws btn-teal"
                          onClick={() => setAction("transport_accounts_final")}
                          data-testid="transport-accounts-final-button">
                    <CheckCircle2 size={16} /> Mark Debited & Complete
                  </button>
                ) : isPrintPOStage ? (
                  // Phase 20.2 — Print PO panel is the only action at this
                  // stage; the standard approve/hold/reject buttons are
                  // suppressed because the PO uses the dropdown modal.
                  null
                ) : (
                  <>
                    <button type="button" className="btn-vgws btn-teal" onClick={() => setAction("approve")} data-testid="approve-button">
                      <CheckCircle2 size={16} /> Approve
                    </button>
                    <button type="button" className="btn-vgws bg-yellow-600 border-yellow-700 hover:bg-yellow-700" onClick={() => setAction("on_hold")} data-testid="on-hold-button">
                      <PauseCircle size={16} /> Place on hold
                    </button>
                    <button type="button" className="btn-vgws btn-danger" onClick={() => setAction("reject")} data-testid="reject-button">
                      <XCircle size={16} /> Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {canResubmit && (
            <div className="sheet p-5 border-t-yellow-500" data-testid="resubmit-panel">
              <h3 className="font-serif text-lg font-semibold text-[#7a1f2b] mb-2">Resubmit required</h3>
              <p className="text-sm text-[#4a524c] mb-3">
                This requisition was placed on hold. Read the remarks in the audit trail, then resubmit to restart the approval chain.
              </p>
              <ResubmitButton req={req} onDone={load} />
            </div>
          )}

          {(canAmendOwnDecision || canAmendAnyStage) && (
            <div className="sheet p-5 border-t-[#b48a45]" data-testid="amend-panel">
              <h3 className="font-serif text-lg font-semibold text-[#7a1f2b] mb-2">
                Amend decision
              </h3>
              <p className="text-sm text-[#4a524c] mb-3">
                {canAmendAnyStage
                  ? "As super admin, you may amend any stage decision on this requisition. The workflow will rewind to the chosen stage."
                  : "You can change your prior decision (approve / on hold / reject). The workflow will rewind to the amended stage."}
              </p>
              <div className="flex flex-wrap gap-2">
                {(canAmendAnyStage
                  ? (req.audit_trail || []).filter(a => _amendableActions.has(a?.action))
                  : myPriorActions
                ).slice().reverse().slice(0, 5).map((entry, idx) => (
                  <button key={idx} type="button" className="btn-vgws btn-ghost text-xs"
                          onClick={() => { setAmendPrior(entry); setShowAmend(true); }}
                          data-testid={`amend-my-decision-btn-${idx}`}>
                    Amend: {entry.action} · {STAGE_LABEL[entry.stage] || entry.stage}
                    {canAmendAnyStage && entry.actor_name ? ` · ${entry.actor_name}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showAmend && amendPrior && (
            <AmendDecisionModal
              open={showAmend}
              requisition={req}
              previousAction={amendPrior.action}
              previousRemarks={amendPrior.remarks || ""}
              onClose={() => { setShowAmend(false); setAmendPrior(null); }}
              onDone={() => { setShowAmend(false); setAmendPrior(null); load(); }}
            />
          )}

          <div className="sheet p-5">
            <h3 className="font-serif text-lg font-semibold text-[#7a1f2b] mb-3">Audit trail</h3>
            <AuditTrail entries={req.audit_trail || []} refCode={req.ref_code} onResent={load} />
          </div>

          {req.drive_folder_link && (
            <div className="sheet p-5 border-t-[#2d6b6b]" data-testid="drive-panel">
              <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
                Google Drive
              </div>
              <h3 className="font-serif text-lg font-semibold text-[#1f2320] mt-1 mb-2">
                Requisition folder
              </h3>
              <p className="text-xs text-[#6e7770] mb-3">
                Form PDF, applicant attachments and (on completion) audit trail + bills are
                auto-synced here.
              </p>
              <a
                href={req.drive_folder_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-vgws btn-teal w-full justify-center"
                data-testid="view-in-drive-button"
              >
                <ExternalLink size={16} /> View in Google Drive
              </a>
              <div className="mt-2 text-[11px] text-[#6e7770] leading-snug"
                    data-testid="drive-link-note">
                For view / download / print only. Please request access if you
                need to edit.
              </div>
            </div>
          )}

          {isSuperAdmin(user) && (
            <SuperAdminPanel req={req} onDone={load} />
          )}
        </aside>
      </div>

      {action && (
        isV22POAckStage && action === "transport_po_acknowledge" ? (
          <TransportPOAckModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isV22AccountsAckStage && action === "transport_accounts_ack" ? (
          <TransportAccountsAckModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isV22FinalPOStage && action === "transport_final_po_fulfill" ? (
          <TransportFinalPOFulfillModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isV22AccountsFinalStage && action === "transport_accounts_final" ? (
          <TransportAccountsFinalModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isTransportPOStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <TransportPOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isServicePOStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <ServicePOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isFnbPOStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <FnBPOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isAdmin1PricingStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <Admin1PricingModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isAccountsPOStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <AccountsPOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isPrintPOStage && action === "print_po_update" ? (
          <PrintPOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : isFinalPOStage && action === "mark_fulfilled" ? (
          <FinalPOFanoutModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            currentUser={user}
            onDone={setReq}
          />
        ) : isStoresPOStage && ["approve", "on_hold", "reject"].includes(action) ? (
          <StoresPOActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : (isV10AccountsReview || isV13AccountsReview) && action === "mark_fulfilled" ? (
          <FinalBillsModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            currentUser={user}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
            onDone={setReq}
          />
        ) : (isProcessOwnerReview || isAccountsReview || isV13AccountsReview) && action === "mark_fulfilled" ? (
          <SingleFulfilmentModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            currentUser={user}
            stageLabel={STAGE_LABEL[req.current_stage]}
            closesOnMark={isAccountsReview || req.form_type === "maini"}
            onDone={setReq}
          />
        ) : action === "set_completion_status" ? (
          <CompletionToggleModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            onDone={setReq}
          />
        ) : (
          <ActionModal
            open={!!action}
            onClose={() => setAction(null)}
            requisition={req}
            action={action}
            subStatusOptions={config.process_owner_sub_statuses}
            onDone={setReq}
            actingAsBanner={superBypassing ? STAGE_LABEL[req.current_stage] : null}
          />
        )
      )}

      {showEdit && (
        <EditSectionModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          onDone={setReq}
          requisition={req}
        />
      )}
    </Layout>
  );
}

function ResubmitButton({ req, onDone }) {
  const [busy, setBusy] = useState(false);
  const doIt = async () => {
    setBusy(true);
    try {
      await api.post(`/requisitions/${req.ref_code}/resubmit`, { form_type: req.form_type, form_data: {} });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <button type="button" className="btn-vgws" onClick={doIt} disabled={busy} data-testid="resubmit-button">
      {busy ? "Resubmitting…" : "Resubmit as-is"}
    </button>
  );
}


/* ============================= SUPER ADMIN PANEL ============================= */
const STAGE_CHOICES = [
  "hod_review", "admin1_review", "admin2_review",
  "process_owner", "accounts", "completed", "rejected", "on_hold", "draft",
];

function SuperAdminPanel({ req, onDone }) {
  const [mode, setMode] = useState(null); // edit | reassign | force | delete | restore
  const isDeleted = !!req.deleted_at;

  return (
    <div className="sheet p-5 border-t-[#7a1f2b] border-t-4" data-testid="super-admin-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert size={16} className="text-[#7a1f2b]" />
        <div className="text-[11px] uppercase tracking-widest text-[#7a1f2b] font-semibold">
          Super Admin Controls
        </div>
      </div>
      <h3 className="font-serif text-lg font-semibold text-[#1f2320] mb-2">Override tools</h3>
      <p className="text-xs text-[#4a524c] mb-3">
        Every action is logged to the audit trail. Remarks are mandatory (min 10 chars).
      </p>

      {isDeleted && (
        <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded mb-3" data-testid="deleted-banner">
          <strong>Permanently deleted.</strong> {req.deleted_by_name} on {formatDate(req.deleted_at, { includeTime: true })} — {req.deleted_reason}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {!isDeleted && (
          <>
            <button className="btn-ghost text-xs" onClick={() => setMode("edit")} data-testid="sa-edit-btn">
              <PenLine size={12} /> Edit fields
            </button>
            <button className="btn-ghost text-xs" onClick={() => setMode("reassign")} data-testid="sa-reassign-btn">
              <Users size={12} /> Reassign
            </button>
            <button className="btn-ghost text-xs" onClick={() => setMode("force")} data-testid="sa-force-btn">
              <GitBranch size={12} /> Force stage
            </button>
            <button className="btn-ghost text-xs !text-[#b3261e] !border-[#b3261e]/40"
                    title="Permanently delete (cannot be restored)"
                    onClick={() => setMode("delete")} data-testid="sa-delete-btn">
              <Trash2 size={12} /> Delete Permanently
            </button>
          </>
        )}
        {isDeleted && (
          <button className="btn-ghost text-xs col-span-2 !text-[#2d6b6b] !border-[#2d6b6b]/40"
                  onClick={() => setMode("restore")} data-testid="sa-restore-btn">
            <RotateCcw size={12} /> Restore
          </button>
        )}
      </div>

      {mode === "edit"     && <EditModal req={req} onClose={() => setMode(null)} onDone={onDone} />}
      {mode === "reassign" && <ReassignModal req={req} onClose={() => setMode(null)} onDone={onDone} />}
      {mode === "force"    && <ForceStageModal req={req} onClose={() => setMode(null)} onDone={onDone} />}
      {mode === "delete"   && <DeleteModal req={req} onClose={() => setMode(null)} onDone={onDone} />}
      {mode === "restore"  && <RestoreModal req={req} onClose={() => setMode(null)} onDone={onDone} />}
    </div>
  );
}

function ModalShell({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         data-testid={testId} onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2d7c3] bg-[#faf5eb] flex items-center gap-2">
          <ShieldAlert className="text-[#7a1f2b]" size={18} />
          <h3 className="font-serif text-lg font-semibold text-[#1f2320]">{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function RemarksField({ value, onChange, testId }) {
  const trimmed = (value || "").trim();
  const ok = trimmed.length >= 10;
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
        Remarks <span className="text-[#b3261e]">*</span>
        <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 10 chars</span>
      </span>
      <textarea rows={3} className={`input-vgws ${!ok && trimmed.length > 0 ? "!border-[#b3261e]" : ""}`}
                value={value} onChange={(e) => onChange(e.target.value.slice(0, 1000))}
                data-testid={testId} />
      <div className="mt-1 flex items-center justify-between text-xs">
        <span className={ok ? "text-[#2d6b6b]" : "text-[#b3261e]"}>{ok ? "Looks good." : "Mandatory — describe the reason."}</span>
        <span className="font-mono text-[#6e7770]">{trimmed.length} / 1000</span>
      </div>
    </label>
  );
}

function EditModal({ req, onClose, onDone }) {
  const schema = getReeditSchema(req.form_type, req.form_data);
  const [values, setValues] = useState(() => ({ ...(req.form_data || {}) }));
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const remarksOk = remarks.trim().length >= 10;

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const setField = (k, v) => setValues((cur) => ({ ...cur, [k]: v }));

  // Only send the diff so untouched fields remain intact server-side.
  const diff = () => {
    const orig = req.form_data || {};
    const out = {};
    for (const f of schema) {
      const k = f.key;
      if (JSON.stringify(values[k]) !== JSON.stringify(orig[k])) out[k] = values[k];
    }
    return out;
  };

  const submit = async () => {
    if (!remarksOk) { toast.error("Remarks are mandatory (min 10 chars)"); return; }
    const patch = diff();
    if (Object.keys(patch).length === 0) { toast.error("No fields changed."); return; }
    setBusy(true);
    try {
      await api.patch(`/requisitions/${req.ref_code}`, { form_data: patch, remarks: remarks.trim() });
      toast.success("Form fields updated");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const subDeptOpts = (depts.find((d) => d.name === values.mainDept) || {}).sub_departments || [];

  const renderInput = (f) => {
    const v = values[f.key];
    const tid = `sa-edit-${f.key}`;
    if (f.type === "textarea") {
      return <textarea rows={3} className="input-vgws" value={v ?? ""}
                        onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "number") {
      return <input type="number" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "date") {
      return <input type="date" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "time") {
      return <input type="time" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "checkbox") {
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!v}
                  onChange={(e) => setField(f.key, e.target.checked)} data-testid={tid} />
          <span>{v ? "Yes" : "No"}</span>
        </label>
      );
    }
    if (f.type === "select") {
      let opts = f.options || [];
      if (f.optionsFrom === "mainDept") opts = depts.map((d) => d.name);
      if (f.optionsFrom === "subDept") opts = subDeptOpts;
      if (f.optionsFrom === "hods") opts = hods.map((h) => h.email);
      return (
        <select className="input-vgws" value={v ?? ""}
                 onChange={(e) => setField(f.key, e.target.value)} data-testid={tid}>
          <option value="">— select —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "rows") {
      const rows = Array.isArray(v) ? v : [];
      const addRow = () => {
        if (rows.length >= 10) return;
        const blank = Object.fromEntries(f.rows.map((c) => [c.key, ""]));
        if (f.key === "bills") blank.sr_no = String(rows.length + 1);
        setField(f.key, [...rows, blank]);
      };
      const rmRow = (i) => setField(f.key, rows.filter((_, idx) => idx !== i));
      const setCell = (i, ck, cv) => setField(f.key,
        rows.map((r, idx) => (idx === i ? { ...r, [ck]: cv } : r)));
      return (
        <div className="rounded-md border border-[#e2d7c3] bg-white">
          <table className="w-full text-xs">
            <thead className="bg-[#faf5eb] text-[#5a1520]">
              <tr>
                {f.rows.map((c) => <th key={c.key} className="text-left p-1 pl-2">{c.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={f.rows.length + 1} className="p-2 text-[#6e7770] italic">No rows</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={i}>
                  {f.rows.map((c) => (
                    <td key={c.key} className="p-1 pl-2">
                      <input className="input-vgws py-1 text-xs" value={row[c.key] ?? ""}
                              onChange={(e) => setCell(i, c.key, e.target.value)}
                              data-testid={`sa-edit-${f.key}-${i}-${c.key}`} />
                    </td>
                  ))}
                  <td className="p-1">
                    <button type="button" className="row-del" onClick={() => rmRow(i)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-1 border-t border-[#e2d7c3]">
            <button type="button" className="btn-ghost text-xs" onClick={addRow}>Add row</button>
          </div>
        </div>
      );
    }
    return <input className="input-vgws" value={v ?? ""}
                   onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
  };

  return (
    <ModalShell title={`Edit fields · ${req.ref_code}`} onClose={onClose} testId="sa-edit-modal">
      <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
        <div className="text-xs text-[#4a524c] bg-[#faf5eb] px-3 py-2 rounded border-l-4 border-[#b48a45]">
          Super-admin edit. Only fields you change here will be updated;
          all others remain untouched. A snapshot of the previous version
          is preserved automatically.
        </div>
        {schema.length === 0 && (
          <div className="text-xs text-[#b3261e]">
            No edit schema found for form_type <code>{req.form_type}</code>.
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {schema.map((f) => (
            <div key={f.key} className={f.type === "rows" || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-semibold text-[#4a524c] mb-1">
                {f.label}{f.required && <span className="text-[#b3261e]"> *</span>}
              </label>
              {renderInput(f)}
            </div>
          ))}
        </div>
        <RemarksField value={remarks} onChange={setRemarks} testId="sa-edit-remarks" />
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={submit} disabled={busy || !remarksOk}
                className={`btn-vgws ${(!remarksOk || busy) ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="sa-edit-save">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </ModalShell>
  );
}

function ReassignModal({ req, onClose, onDone }) {
  const [stage, setStage] = useState("hod");
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("replace");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);
  const filtered = users.filter((u) => {
    if (stage === "hod") return u.role === "hod";
    if (stage === "process_owner") return u.role === "process_owner";
    return ["admin1", "admin1_alt", "admin2", "super_admin"].includes(u.role);
  });
  const submit = async () => {
    if (!userId && action !== "remove") { toast.error("Pick a user"); return; }
    if (remarks.trim().length < 10) { toast.error("Remarks are mandatory (min 10 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/requisitions/${req.ref_code}/reassign`, {
        stage, user_id: userId, action, remarks: remarks.trim(),
      });
      toast.success("Reassignment recorded");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Reassign · ${req.ref_code}`} onClose={onClose} testId="sa-reassign-modal">
      <div className="px-5 py-4 space-y-3">
        <label className="block">
          <span className="block text-sm font-medium mb-1.5">Which slot</span>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className="input-vgws" data-testid="sa-reassign-stage">
            <option value="hod">HOD</option>
            <option value="admin1">Admin-1 (Bursar / CAO)</option>
            <option value="admin1_alt">Admin-1 alternate</option>
            <option value="admin2">Admin-2 (HOS)</option>
            <option value="process_owner">Process Owner</option>
          </select>
        </label>
        {stage === "process_owner" && (
          <label className="block">
            <span className="block text-sm font-medium mb-1.5">Action</span>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="input-vgws" data-testid="sa-reassign-action">
              <option value="replace">Replace all with this user</option>
              <option value="add">Add this user</option>
              <option value="remove">Remove this user</option>
            </select>
          </label>
        )}
        <label className="block">
          <span className="block text-sm font-medium mb-1.5">Target user</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="input-vgws" data-testid="sa-reassign-user">
            <option value="">— Select —</option>
            {filtered.map((u) => (
              <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
            ))}
          </select>
        </label>
        <RemarksField value={remarks} onChange={setRemarks} testId="sa-reassign-remarks" />
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-vgws" data-testid="sa-reassign-save">
          <Users size={14} /> {busy ? "Saving…" : "Reassign"}
        </button>
      </div>
    </ModalShell>
  );
}

function ForceStageModal({ req, onClose, onDone }) {
  const [toStage, setToStage] = useState(req.current_stage);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (remarks.trim().length < 10) { toast.error("Remarks are mandatory (min 10 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/requisitions/${req.ref_code}/force-stage`, {
        to_stage: toStage, remarks: remarks.trim(),
      });
      toast.success(`Moved to ${toStage}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Force stage · ${req.ref_code}`} onClose={onClose} testId="sa-force-modal">
      <div className="px-5 py-4 space-y-3">
        <div className="text-xs text-white bg-[#7a1f2b] px-3 py-2 rounded">
          <strong>Danger.</strong> This bypasses the approval chain. Only use for emergencies.
        </div>
        <label className="block">
          <span className="block text-sm font-medium mb-1.5">Target stage</span>
          <select value={toStage} onChange={(e) => setToStage(e.target.value)} className="input-vgws" data-testid="sa-force-stage">
            {STAGE_CHOICES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL[s] || s}</option>
            ))}
          </select>
          <div className="mt-1 text-[11px] text-[#6e7770]">Currently at <strong>{STAGE_LABEL[req.current_stage] || req.current_stage}</strong>.</div>
        </label>
        <RemarksField value={remarks} onChange={setRemarks} testId="sa-force-remarks" />
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-vgws" data-testid="sa-force-save">
          <GitBranch size={14} /> {busy ? "Saving…" : "Force transition"}
        </button>
      </div>
    </ModalShell>
  );
}

function DeleteModal({ req, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (remarks.trim().length < 10) { toast.error("Remarks are mandatory (min 10 chars)"); return; }
    setBusy(true);
    try {
      await api.delete(`/requisitions/${req.ref_code}`, {
        params: { remarks: remarks.trim(), confirm: "yes" },
      });
      toast.success("Requisition permanently deleted");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Delete Permanently · ${req.ref_code}`} onClose={onClose} testId="sa-delete-modal">
      <div className="px-5 py-4 space-y-3">
        <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded">
          <strong>This is a permanent hard delete.</strong> The requisition, its notification schedule and any Drive PDFs are removed. This cannot be restored. All super admins will be notified by email.
        </div>
        <RemarksField value={remarks} onChange={setRemarks} testId="sa-delete-remarks" />
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-vgws btn-danger" data-testid="sa-delete-confirm">
          <Trash2 size={14} /> {busy ? "Deleting…" : "Delete Permanently"}
        </button>
      </div>
    </ModalShell>
  );
}

function RestoreModal({ req, onClose, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (remarks.trim().length < 10) { toast.error("Remarks are mandatory (min 10 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/requisitions/${req.ref_code}/restore`, { remarks: remarks.trim() });
      toast.success("Requisition restored");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Restore · ${req.ref_code}`} onClose={onClose} testId="sa-restore-modal">
      <div className="px-5 py-4 space-y-3">
        <RemarksField value={remarks} onChange={setRemarks} testId="sa-restore-remarks" />
      </div>
      <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-vgws btn-teal" data-testid="sa-restore-confirm">
          <RotateCcw size={14} /> {busy ? "Restoring…" : "Restore"}
        </button>
      </div>
    </ModalShell>
  );
}
